import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ASSETS } from '@swim/config'
import { OFFER_TYPE_COLORS } from './OfferTypeChip'
import { buildOfferImageSrcSet } from '../../lib/offer-image-srcset'
import type { PublicOffer } from '../../types/offers'

export const TYPE_CLASSES: Record<string, string> = OFFER_TYPE_COLORS

interface OfferImagePanelProps {
  offer: PublicOffer
  /** Tailwind classes for sizing the panel — e.g. "h-36 w-full" or "h-16 w-24" */
  className?: string
  /** Show the type chip over the image. Default: true */
  showChip?: boolean
  /** CSS media query sizes for responsive image selection (required when images[] is present) */
  sizes?: string
}

/**
 * Image panel that supports responsive image variants via srcset when available,
 * falling back to CSS background-image for legacy single-image offers.
 *
 * When offer.images[] is present: renders a real <img> with srcset/sizes
 * When offer.images[] is absent: uses CSS background-image (legacy path)
 *
 * No image → sand background + SWIM stacked logo watermark at 25% opacity.
 * Uses black logo in light mode, white logo in dark mode.
 *
 * Used by: OfferCard (30% panel, chip on), OfferListRow (thumbnail, chip off),
 * OfferCompactRow (10% thumbnail, chip off).
 */
export function OfferImagePanel({
  offer,
  className = '',
  showChip = true,
  sizes,
}: OfferImagePanelProps) {
  const { t } = useTranslation('offers')
  const hasStructuredImages = Boolean(offer.images && offer.images.length > 0)
  const hasImage = Boolean(offer.imageUrl)
  const [imgError, setImgError] = useState(false)

  return (
    <div
      className={`relative overflow-hidden bg-sand-200 dark:bg-slate-700 ring-1 ring-inset ring-sand-300 dark:ring-slate-600 ${className}`}
      style={hasImage && !hasStructuredImages ? {
        backgroundImage: `url(${offer.imageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      } : undefined}
    >
      {/* Responsive image with srcset — rendered when structured variants exist */}
      {hasStructuredImages && !imgError && offer.images && (
        <img
          src={offer.images[0].md}
          srcSet={buildOfferImageSrcSet(offer.images[0])}
          sizes={sizes}
          alt={offer.title}
          loading="lazy"
          className="w-full h-full object-cover"
          onError={() => setImgError(true)}
        />
      )}

      {/* Watermark — shown when no image or image load error */}
      {(!hasImage || imgError) && (
        <>
          {/* Light mode: black logo */}
          <img
            src={ASSETS.logos.stacked.black.sm}
            alt=""
            aria-hidden
            className="absolute inset-0 m-auto w-1/2 max-w-[64px] opacity-25 dark:hidden select-none pointer-events-none"
          />
          {/* Dark mode: white logo */}
          <img
            src={ASSETS.logos.stacked.white.sm}
            alt=""
            aria-hidden
            className="absolute inset-0 m-auto w-1/2 max-w-[64px] opacity-25 hidden dark:block select-none pointer-events-none"
          />
        </>
      )}

      {showChip && offer.offerType !== 'HYBRID' && (
        <span
          data-testid="offer-type-chip"
          className={`absolute top-3 start-3 inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${TYPE_CLASSES[offer.offerType]}`}
        >
          {t(`fields.offerType.${offer.offerType}`)}
        </span>
      )}
    </div>
  )
}
